import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import AppShell from "@/pages/AppShell";
import { api } from "@/lib/api";

const MAX_ATTEMPTS = 8;
const POLL_MS = 2500;

export default function BillingSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const provider = params.get("provider"); // 'mayar' | 'ipaymu'
  const orderId = params.get("order_id");
  const navigate = useNavigate();
  const [state, setState] = useState({ status: "checking", payment_status: null, amount_total: null, currency: "idr" });
  const [attempts, setAttempts] = useState(0);
  const stopped = useRef(false);

  // Redirect-style gateways (Mayar, iPaymu) come back with ?provider=&order_id=
  // and confirm asynchronously via webhook — we poll /billing/status.
  const isRedirectProvider = (provider === "mayar" || provider === "ipaymu") && !!orderId;
  const isMayar = isRedirectProvider;

  useEffect(() => {
    if (isMayar) {
      // Poll /billing/status to see whether the webhook activated the plan.
      const tick = async (n) => {
        if (stopped.current) return;
        try {
          const r = await api.get("/billing/status");
          const data = r.data || {};
          const activated = data.plan && data.plan !== "free" && data.plan_active_until;
          setState({ status: activated ? "complete" : "open", payment_status: activated ? "paid" : "unpaid" });
          setAttempts(n);
          if (activated) { stopped.current = true; return; }
          if (n >= MAX_ATTEMPTS) { stopped.current = true; return; }
          setTimeout(() => tick(n + 1), POLL_MS);
        } catch (err) {
          stopped.current = true;
          setState({ status: "error", payment_status: null });
        }
      };
      tick(1);
      return () => { stopped.current = true; };
    }

    if (!sessionId) {
      setState({ status: "error", payment_status: null });
      return;
    }
    const tick = async (n) => {
      if (stopped.current) return;
      try {
        const r = await api.get(`/stripe/status/${sessionId}`);
        const data = r.data || {};
        setState(data);
        setAttempts(n);
        if (data.payment_status === "paid") {
          stopped.current = true;
          return;
        }
        if (data.status === "expired" || data.status === "canceled") {
          stopped.current = true;
          return;
        }
        if (n >= MAX_ATTEMPTS) {
          stopped.current = true;
          return;
        }
        setTimeout(() => tick(n + 1), POLL_MS);
      } catch (err) {
        stopped.current = true;
        setState({ status: "error", payment_status: null });
        return;
      }
    };
    tick(1);
    return () => { stopped.current = true; };
  }, [sessionId, isMayar]);

  const isPaid = state.payment_status === "paid";
  const isPending = !isPaid && (state.status === "open" || state.payment_status === "unpaid" || state.payment_status === null) && attempts < MAX_ATTEMPTS;
  const isExpired = state.status === "expired" || state.status === "canceled";
  const isError = state.status === "error" || (!isPaid && !isPending && !isExpired);

  const providerLabel = provider || "ipaymu";

  return (
    <AppShell title="Payment" subtitle={`// ${providerLabel}`}>
      <div data-testid="billing-success-page" className="max-w-xl">
        {!sessionId && !isMayar && (
          <div className="border border-border bg-card p-8">
            <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground">missing session</div>
            <h2 className="mt-2 font-marketing text-3xl tracking-tight">No checkout session in URL.</h2>
            <Link to="/billing" className="mt-6 inline-block px-6 py-3 bg-obsidian text-bone font-app font-bold uppercase tracking-wider hover:bg-intl transition-colors">Back to billing</Link>
          </div>
        )}

        {(sessionId || isMayar) && (
          <div className="border border-border bg-card p-8" data-testid={`${providerLabel}-result-${isPaid ? "paid" : (isExpired ? "expired" : (isError ? "error" : "pending"))}`}>
            <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground">// {isPaid ? "payment successful" : (isExpired ? "session expired" : (isError ? "payment failed" : "verifying payment"))}</div>
            <h2 className="mt-2 font-marketing text-3xl tracking-tight">
              {isPaid && "Thank you. Your plan is active."}
              {isPending && (isRedirectProvider ? `Waiting for ${providerLabel} to confirm payment…` : "Verifying payment…")}
              {isExpired && "Checkout session expired or was cancelled."}
              {isError && "We couldn't confirm your payment."}
            </h2>
            {isPending && (
              <div className="mt-6 inline-block w-8 h-8 border-2 border-obsidian border-t-transparent rounded-full animate-spin" />
            )}
            {!isMayar && (state.amount_total != null) && (
              <div className="mt-6 font-mono text-sm text-muted-foreground">
                Amount: {(state.amount_total / 100).toFixed(2)} {String(state.currency).toUpperCase()}
              </div>
            )}
            {isMayar && isPending && (
              <p className="mt-4 font-mono text-xs text-muted-foreground max-w-prose">
                If you&apos;ve completed the payment in Mayar, your plan will activate within a few seconds once the webhook is delivered. You can safely return to the dashboard.
              </p>
            )}
            <div className="mt-8 flex gap-3">
              <button
                onClick={() => navigate("/dashboard")}
                data-testid="billing-success-go-dashboard"
                className="px-6 py-3 bg-obsidian text-bone font-app font-bold uppercase tracking-wider hover:bg-intl transition-colors"
              >
                Open dashboard
              </button>
              <Link to="/billing" className="px-6 py-3 border border-border font-app uppercase text-xs tracking-widest hover:bg-obsidian hover:text-bone hover:border-obsidian transition-colors flex items-center">
                Back to billing
              </Link>
            </div>
          </div>
        )}
      </div>
    </AppShell>
  );
}
